import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable } from 'rxjs';
import { LocationPickerComponent } from '../component/location-picker/location-picker.component';
import { Coordinate, MapType, PooledMapEntry } from '../interface/google-map.interface';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapService {
  /** Pool of reusable map/panorama instances keyed by MapType */
  private readonly _pool = new Map<MapType, PooledMapEntry[]>();

  /** Off-screen host where pooled containers are parked when not in use */
  private _hiddenHost?: HTMLDivElement;

  constructor(private _matDialog: MatDialog) {}

  /**
   * Acquires a pooled map or street-view container, moves it into the given host element,
   * and returns the pool entry. Reuses a free entry if available, otherwise creates a new one.
   */
  public acquireMapView(
    type: MapType,
    host: HTMLElement,
    options: {
      center: google.maps.LatLng;
      mapId: string;
      zoom?: number;
      gestureHandling?: string;
      title?: string;
    }
  ): PooledMapEntry {
    let entries = this._pool.get(type);
    if (!entries) {
      entries = [];
      this._pool.set(type, entries);
    }

    let entry = entries.find((e) => !e.inUse);
    if (!entry) {
      entry = this._createEntry(type, options);
      entries.push(entry);
    }
    entry.inUse = true;

    // Move the container into the requesting component's host
    host.appendChild(entry.container);

    // Reconfigure the instance for the new location
    if (type === 'streetView') {
      // Hide until the component's strict lookup binds a match; the pooled instance may still show a previous panorama.
      (entry.instance as google.maps.StreetViewPanorama).setVisible(false);
    } else {
      const mapInstance = entry.instance as google.maps.Map;
      mapInstance.setCenter(options.center);
      mapInstance.setZoom(options.zoom ?? 18);
      mapInstance.setMapTypeId(type === 'roadmap' ? google.maps.MapTypeId.ROADMAP : google.maps.MapTypeId.SATELLITE);

      // Update or create the marker
      if (entry.marker) {
        entry.marker.position = options.center;
        entry.marker.title = options.title ?? '';
        entry.marker.map = mapInstance;
      } else {
        entry.marker = new google.maps.marker.AdvancedMarkerElement({
          map: mapInstance,
          position: options.center,
          title: options.title,
        });
      }
    }

    // Trigger a resize so the map renders within the new container dimensions
    google.maps.event.trigger(entry.instance, 'resize');

    return entry;
  }

  /** Releases a pooled map entry back to the hidden off-screen host */
  public releaseMapView(entry: PooledMapEntry): void {
    if (!entry) return;
    entry.inUse = false;

    // Detach marker from the map
    if (entry.marker) {
      entry.marker.map = null;
    }

    // Hide street view to stop rendering
    if (entry.instance instanceof google.maps.StreetViewPanorama) {
      entry.instance.setVisible(false);
    }

    // Park the container off-screen
    this._getHiddenHost().appendChild(entry.container);
  }

  /** Opens a Location picker and returns the confirmed coordinate, or null when the dialog is dismissed */
  public openLocationPicker(center?: Coordinate): Observable<Coordinate | null> {
    const dialogRef = this._matDialog.open<LocationPickerComponent, { center?: Coordinate | null }, Coordinate>(
      LocationPickerComponent,
      {
        data: { center },
        width: '90vw',
        maxWidth: '800px',
        panelClass: 'location-picker-dialog',
        autoFocus: false,
      }
    );

    return dialogRef.afterClosed().pipe(map((value) => value ?? null));
  }

  private _createEntry(
    type: MapType,
    options: {
      center: google.maps.LatLng;
      mapId: string;
      zoom?: number;
      gestureHandling?: string;
      title?: string;
    }
  ): PooledMapEntry {
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.height = '100%';

    if (type === 'streetView') {
      const pano = new google.maps.StreetViewPanorama(container, {
        addressControl: false,
        scrollwheel: false,
        disableDefaultUI: true,
        fullscreenControl: true,
        visible: false,
      });
      return { container, instance: pano, inUse: false };
    }

    const mapInstance = new google.maps.Map(container, {
      mapId: options.mapId,
      center: options.center,
      mapTypeId: type === 'roadmap' ? google.maps.MapTypeId.ROADMAP : google.maps.MapTypeId.SATELLITE,
      disableDefaultUI: true,
      fullscreenControl: true,
      zoom: options.zoom ?? 18,
      gestureHandling: options.gestureHandling ?? 'cooperative',
      clickableIcons: false,
      zoomControl: false,
      controlSize: 30,
      streetViewControl: true,
      mapTypeControl: true,
      restriction: { latLngBounds: { north: 75, south: 0, east: -60, west: -179.9 } },
      minZoom: 3,
    });

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: mapInstance,
      position: options.center,
      title: options.title,
    });

    return { container, instance: mapInstance, marker, inUse: false };
  }

  /** Lazily creates and returns the hidden off-screen host element */
  private _getHiddenHost(): HTMLDivElement {
    if (!this._hiddenHost) {
      this._hiddenHost = document.createElement('div');
      this._hiddenHost.style.cssText =
        'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none;';
      document.body.appendChild(this._hiddenHost);
    }
    return this._hiddenHost;
  }
}
